import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef } from 'react'
import type { MatchResult } from '../../types'
import {
  DIFF_STATUS_LABELS,
  MATCH_TYPE_LABELS,
  formatConfidence,
  formatMoney,
  formatNumber,
  primaryRow,
  sourceRowLabel,
} from '../../ui/formatters'

interface VirtualDiffTableProps {
  onSelect: (result: MatchResult) => void
  results: readonly MatchResult[]
  selectedId: string | null
}

function changeText(
  baseline: string | number | null,
  target: string | number | null,
  kind: 'number' | 'money',
): string {
  const formatter = kind === 'money' ? formatMoney : formatNumber
  return `${formatter(typeof baseline === 'number' ? baseline : null)} → ${formatter(
    typeof target === 'number' ? target : null,
  )}`
}

export function VirtualDiffTable({ onSelect, results, selectedId }: VirtualDiffTableProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: results.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 62,
    overscan: 8,
  })

  return (
    <section className="result-table-shell" aria-label="差异结果列表">
      <div
        ref={scrollRef}
        className="result-table-scroll"
        role="region"
        aria-label="可横向滚动的差异结果表"
        tabIndex={0}
      >
        <div
          className="result-table-grid"
          role="table"
          aria-label="清单差异"
          aria-colcount={7}
          aria-rowcount={results.length + 1}
        >
          <div className="result-table-head" role="rowgroup">
            <div className="result-table-header" role="row" aria-rowindex={1}>
              <span role="columnheader">状态</span>
              <span role="columnheader">项目编码与名称</span>
              <span role="columnheader">工程量</span>
              <span role="columnheader">综合单价</span>
              <span role="columnheader">金额影响</span>
              <span role="columnheader">匹配依据</span>
              <span role="columnheader">来源行</span>
            </div>
          </div>
          {results.length > 0 ? (
            <div
              className="result-table-virtual"
              role="rowgroup"
              style={{ height: virtualizer.getTotalSize() }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const result = results[virtualRow.index]
                const row = primaryRow(result)
                const quantity = result.fieldChanges.quantity
                const unitPrice = result.fieldChanges.unitPrice
                const rowLabel = `${row?.itemCode.length ? row.itemCode : '无编码'} ${
                  row?.itemName.length ? row.itemName : '未命名项目'
                }`
                return (
                  <div
                    className="result-row"
                    data-selected={selectedId === result.id}
                    data-status={result.status}
                    key={result.id}
                    role="row"
                    aria-rowindex={virtualRow.index + 2}
                    style={{
                      height: virtualRow.size,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <span role="cell">
                      <em className={`diff-badge diff-badge--${result.status}`}>
                        {DIFF_STATUS_LABELS[result.status]}
                      </em>
                    </span>
                    <span className="result-row__identity" role="cell">
                      <button
                        className="result-row__trigger"
                        data-testid={`result-row-${result.status}`}
                        type="button"
                        aria-label={`打开详情：${rowLabel}`}
                        onClick={() => onSelect(result)}
                      >
                        <code>{row?.itemCode.length ? row.itemCode : '无编码'}</code>
                        <strong title={row?.itemName}>
                          {row?.itemName.length ? row.itemName : '未命名项目'}
                        </strong>
                      </button>
                    </span>
                    <span
                      className={quantity.changed ? 'change-cell is-changed' : 'change-cell'}
                      role="cell"
                    >
                      {changeText(quantity.baselineValue, quantity.targetValue, 'number')}
                    </span>
                    <span
                      className={unitPrice.changed ? 'change-cell is-changed' : 'change-cell'}
                      role="cell"
                    >
                      {changeText(unitPrice.baselineValue, unitPrice.targetValue, 'money')}
                    </span>
                    <span
                      className={
                        result.amountImpact.value !== null && result.amountImpact.value < 0
                          ? 'impact-cell is-negative'
                          : 'impact-cell'
                      }
                      role="cell"
                    >
                      {formatMoney(result.amountImpact.value)}
                    </span>
                    <span className="match-cell" role="cell">
                      <strong title={result.matchReason}>{result.matchReason}</strong>
                      <small>
                        {MATCH_TYPE_LABELS[result.matchType]} ·{' '}
                        {formatConfidence(result.confidence)}
                      </small>
                    </span>
                    <span className="source-row-cell tabular-number" role="cell">
                      {sourceRowLabel(result)}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : null}
        </div>
        {results.length === 0 ? (
          <div className="result-table-empty" role="status">
            没有符合当前筛选条件的差异。
          </div>
        ) : null}
      </div>
    </section>
  )
}
